import React, { useState, useEffect } from 'react';
import { vehiclesAPI } from '../services/api';
import toast from 'react-hot-toast';

const FIPE_BASE = 'https://parallelum.com.br/fipe/api/v1';
const FIPE_TYPE = { car: 'carros', motorcycle: 'motos', truck: 'caminhoes', suv: 'carros' };

const TYPES = [
  { value: 'car', label: 'Carro', icon: '🚗' },
  { value: 'motorcycle', label: 'Moto', icon: '🏍️' },
  { value: 'truck', label: 'Pickup', icon: '🛻' },
  { value: 'suv', label: 'SUV', icon: '🚙' },
];

/* ── nicov2 modal styles ───────────────── */
const M = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)',
    zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '22px', padding: window.innerWidth <= 480 ? '20px' : '32px',
    maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
    boxSizing: 'border-box',
    boxShadow: '0 20px 48px rgba(15,64,35,.14)',
    animation: 'slideUp .3s ease',
  },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: {
    fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800,
    textTransform: 'uppercase', fontSize: '24px', margin: 0,
    letterSpacing: '-0.01em', color: '#14562e',
  },
  close: {
    width: '36px', height: '36px', borderRadius: '10px', background: '#f4ede0',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', border: 'none', color: '#183d24', transition: 'background 0.15s',
  },
  label: {
    fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '1px', color: '#6a7268', marginBottom: '6px', display: 'block',
  },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid #e5ddc9', background: '#f4ede0', color: '#183d24',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px',
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.15s, background 0.15s',
  },
  select: {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1.5px solid #e5ddc9', background: '#f4ede0', color: '#183d24',
    fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px',
    cursor: 'pointer', outline: 'none', boxSizing: 'border-box',
  },
  grid: { display: 'grid', gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : '1fr 1fr', gap: '16px', marginTop: '16px' },
  row: { display: 'flex', flexDirection: 'column', gap: '6px' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  actions: { display: 'flex', gap: '10px', marginTop: '22px', justifyContent: 'flex-end' },
  btn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', padding: '12px 22px', borderRadius: '12px',
    fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none',
    fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
  },
  fipeBadge: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 14px', background: '#e6f0e9', borderRadius: '10px',
    border: '1px solid rgba(20,86,46,0.15)', marginTop: '16px',
    fontSize: '12px', color: '#14562e', fontWeight: 500,
  },
  tip: {
    fontSize: '12.5px', color: '#6a7268', marginTop: '16px', lineHeight: 1.5,
  },
  manualLink: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: '#e97117', fontWeight: 600, fontSize: '12.5px',
    fontFamily: "'DM Sans', sans-serif", textDecoration: 'underline',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },
  fallbackRow: { marginTop: '10px', display: 'flex', justifyContent: 'flex-start' },
  emptyAlert: {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
    padding: '12px 14px', background: '#fdecd8', borderRadius: '10px',
    border: '1px solid rgba(233,113,23,0.3)', marginTop: '10px',
    fontSize: '12.5px', color: '#7a3d10', lineHeight: 1.5,
  },
};

function Chip({ label, icon, selected, onClick }) {
  return (
    <span onClick={onClick} style={{
      padding: '8px 14px', borderRadius: '999px',
      background: selected ? '#14562e' : '#f4ede0',
      color: selected ? '#fff' : '#183d24',
      fontSize: '13px', fontWeight: 600, cursor: 'pointer',
      border: '1.5px solid transparent', transition: 'all 0.15s',
      display: 'inline-flex', alignItems: 'center', gap: '6px',
    }}>
      {icon && <span>{icon}</span>}{label}
    </span>
  );
}

function formatPlate(value) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
}

export default function VehicleModal({ open, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  // [NEW] Resgatar histórico (sessão 29/ago/2026) — quando o POST /vehicles responde
  // { rescue_available: true }, guardamos aqui o resumo do veículo antigo (pra mostrar
  // "é este o seu veículo?") e o payload já validado (pra reenviar com rescue_action
  // sem o usuário ter que preencher tudo de novo). null = form normal.
  const [rescuePrompt, setRescuePrompt] = useState(null);
  const [form, setForm] = useState({
    type: 'car', brand: '', brandCode: '', model: '', modelCode: '',
    year: new Date().getFullYear(), yearCode: '', color: '',
    nickname: '', plate: '', current_km: '',
    fipe_code: null, fipe_value: null,
    manualEntry: false,
  });

  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fipeType = FIPE_TYPE[form.type] || 'carros';

  // Load brands when type changes
  useEffect(() => {
    if (!open || form.manualEntry) return;
    setLoadingBrands(true);
    setBrands([]); setModels([]); setYears([]);
    setForm(f => ({ ...f, brand: '', brandCode: '', model: '', modelCode: '', yearCode: '', fipe_code: null, fipe_value: null }));
    fetch(`${FIPE_BASE}/${fipeType}/marcas`)
      .then(r => r.json()).then(d => setBrands(Array.isArray(d) ? d : []))
      .catch(() => setBrands([]))
      .finally(() => setLoadingBrands(false));
  }, [form.type, open]);

  // Load models when brand changes
  useEffect(() => {
    if (form.manualEntry) return;
    if (!form.brandCode) { setModels([]); setYears([]); return; }
    setLoadingModels(true);
    setModels([]); setYears([]);
    setForm(f => ({ ...f, model: '', modelCode: '', yearCode: '', fipe_code: null, fipe_value: null }));
    fetch(`${FIPE_BASE}/${fipeType}/marcas/${form.brandCode}/modelos`)
      .then(r => r.json()).then(d => setModels(Array.isArray(d?.modelos) ? d.modelos : []))
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }, [form.brandCode]);

  // Load years when model changes
  useEffect(() => {
    if (form.manualEntry) return;
    if (!form.brandCode || !form.modelCode) { setYears([]); return; }
    setLoadingYears(true);
    setYears([]);
    setForm(f => ({ ...f, yearCode: '', fipe_code: null, fipe_value: null }));
    fetch(`${FIPE_BASE}/${fipeType}/marcas/${form.brandCode}/modelos/${form.modelCode}/anos`)
      .then(r => r.json()).then(d => setYears(Array.isArray(d) ? d : []))
      .catch(() => setYears([]))
      .finally(() => setLoadingYears(false));
  }, [form.modelCode]);

  // Fetch FIPE value when year selected
  useEffect(() => {
    if (form.manualEntry) return;
    if (!form.brandCode || !form.modelCode || !form.yearCode) return;
    fetch(`${FIPE_BASE}/${fipeType}/marcas/${form.brandCode}/modelos/${form.modelCode}/anos/${form.yearCode}`)
      .then(r => r.json())
      .then(d => {
        if (d?.CodigoFipe) {
          const val = parseFloat(d.Valor?.replace('R$ ', '').replace(/\./g, '').replace(',', '.')) || null;
          setForm(f => ({ ...f, fipe_code: d.CodigoFipe, fipe_value: val }));
        }
      }).catch(() => {});
  }, [form.yearCode]);

  if (!open) return null;

  const handleBrandSelect = (e) => {
    const sel = brands.find(b => b.codigo === e.target.value);
    setForm(f => ({ ...f, brand: sel?.nome || '', brandCode: e.target.value, model: '', modelCode: '', yearCode: '' }));
  };

  const handleModelSelect = (e) => {
    const sel = models.find(m => String(m.codigo) === String(e.target.value));
    setForm(f => ({ ...f, model: sel?.nome || '', modelCode: String(e.target.value) }));
  };

  const handleYearSelect = (e) => {
    const sel = years.find(y => y.codigo === e.target.value);
    const yearNum = parseInt(sel?.nome?.split(' ')[0]) || new Date().getFullYear();
    setForm(f => ({ ...f, year: yearNum, yearCode: e.target.value }));
  };

  // Alterna entre busca automática (FIPE) e cadastro manual — reseta os campos dependentes
  const toggleManualEntry = () => {
    setForm(f => ({
      ...f,
      manualEntry: !f.manualEntry,
      brand: '', brandCode: '', model: '', modelCode: '',
      year: new Date().getFullYear(), yearCode: '',
      fipe_code: null, fipe_value: null,
    }));
    setBrands([]); setModels([]); setYears([]);
  };

  const resetForm = () => {
    setForm({
      type: 'car', brand: '', brandCode: '', model: '', modelCode: '',
      year: new Date().getFullYear(), yearCode: '', color: '',
      nickname: '', plate: '', current_km: '', fipe_code: null, fipe_value: null,
      manualEntry: false,
    });
  };

  // [NEW] Faz o POST em si e trata os desfechos possíveis que routes/vehicles.js
  // pode devolver: criado em branco, resgatado (rescued: true — reativou um veículo
  // apagado com a mesma placa) ou "ainda não decidido" (rescue_available: true —
  // pergunta antes de criar/reativar qualquer coisa).
  const submitVehicle = async (payload) => {
    setSaving(true);
    try {
      const res = await vehiclesAPI.create(payload);
      if (res?.rescue_available) {
        setRescuePrompt({ previousVehicle: res.previous_vehicle, payload });
        return;
      }
      // Tenta extrair o id do veículo recém-criado/resgatado em alguns formatos comuns
      // de resposta — usado pelo GaragePage para saber se esse veículo precisa ir
      // direto pro checkout do Stripe.
      const newVehicleId = res?.vehicle?.id || res?.id || res?.data?.vehicle?.id || null;
      toast.success(res?.rescued ? 'Histórico resgatado! 🎉' : 'Veículo adicionado! 🚗');
      // [NEW] Conflito de placa ativa (sessão 12/set/2026) — routes/vehicles.js detectou
      // que essa placa já está cadastrada e ATIVA em outra conta. Não bloqueia nem desfaz
      // o cadastro que acabou de acontecer (ver seção "Conflito de Placa Ativa" no
      // contexto do projeto) — só avisa quem cadastrou, com um tom neutro (não acusa
      // ninguém de má-fé). O dono da conta anterior recebe um email separado, e o
      // backoffice acompanha o caso em "Conflitos de placa". Toast neutro (não
      // toast.error) porque o cadastro em si deu certo.
      if (res?.plate_conflict) {
        toast(
          'Essa placa já consta em outro cadastro no NICO. Seu veículo foi adicionado normalmente — se for engano de algum dos lados, nossa equipe vai analisar.',
          { icon: '⚠️', duration: 7000 }
        );
      }
      resetForm();
      setRescuePrompt(null);
      onSuccess?.(newVehicleId);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Erro ao adicionar veículo');
    } finally { setSaving(false); }
  };

  const handleSubmit = () => {
    if (!form.brand.trim()) { toast.error('Informe a marca'); return; }
    if (!form.model.trim()) { toast.error('Informe o modelo'); return; }
    if (form.plate && form.plate.length !== 7) { toast.error('Placa deve ter 7 caracteres'); return; }

    const payload = {
      type: form.type, brand: form.brand, model: form.model,
      year: form.year, color: form.color || null,
      nickname: form.nickname || null, plate: form.plate || null,
      current_km: parseInt(form.current_km) || 0,
      fipe_code: form.fipe_code || null, fipe_value: form.fipe_value || null,
      // true somente quando o valor veio de fato da tabela FIPE (nunca no cadastro manual)
      fipe_matched: Boolean(form.fipe_code),
    };
    submitVehicle(payload);
  };

  // [NEW] Botões da tela de confirmação de resgate (ver rescuePrompt acima).
  const confirmRescue = () => { if (rescuePrompt) submitVehicle({ ...rescuePrompt.payload, rescue_action: 'confirm' }); };
  const declineRescue = () => { if (rescuePrompt) submitVehicle({ ...rescuePrompt.payload, rescue_action: 'decline' }); };

  const loadingHint = (loading, text) => loading ? `Carregando ${text}...` : `Selecionar ${text}`;

  // Alerta contextual: marca selecionada mas a FIPE não retornou nenhum modelo
  const showEmptyModelsAlert = !form.manualEntry && form.brandCode && !loadingModels && models.length === 0;

  return (
    <div style={M.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={M.modal}>
        {/* Header */}
        <div style={M.head}>
          <h2 style={M.title}>Adicionar veículo</h2>
          <button style={M.close} onClick={() => { setRescuePrompt(null); onClose(); }}
            onMouseOver={e => { e.currentTarget.style.background = '#c94a2f'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#f4ede0'; e.currentTarget.style.color = '#183d24'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* [NEW] Confirmação de "resgatar histórico" — troca o formulário inteiro por essa
            tela enquanto rescuePrompt existir. Ver submitVehicle/confirmRescue/declineRescue
            acima e POST /vehicles em routes/vehicles.js. */}
        {rescuePrompt ? (
          <div>
            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔎</div>
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '18px', color: '#14562e', margin: '0 0 10px' }}>
                Esse veículo já tem histórico no NICO
              </h3>
              <p style={{ color: '#6a7268', fontSize: '13.5px', lineHeight: 1.6, margin: '0 0 6px' }}>
                Encontramos um cadastro anterior com a placa <b style={{ color: '#183d24' }}>{form.plate}</b>:
              </p>
              <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '15px', color: '#183d24', margin: '0 0 18px' }}>
                {rescuePrompt.previousVehicle.brand} {rescuePrompt.previousVehicle.model} {rescuePrompt.previousVehicle.year}
              </p>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#e6f0e9', border: '1px solid rgba(20,86,46,0.15)', borderRadius: '12px', padding: '14px', textAlign: 'left' }}>
                <span style={{ fontSize: '16px', lineHeight: 1 }}>🔒</span>
                <span style={{ fontSize: '12.5px', color: '#14562e', lineHeight: 1.5 }}>
                  Se for o seu veículo, quer resgatar esse histórico? O que já foi registrado continua visível (travado pra edição) e você segue registrando os próximos serviços na sua conta.
                </span>
              </div>
            </div>
            <div style={M.actions}>
              <button style={{ ...M.btn, background: 'transparent', color: '#183d24' }} onClick={declineRescue} disabled={saving}
                onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#ede4d1'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
              >Não, é outro veículo</button>
              <button
                style={{ ...M.btn, background: '#14562e', color: '#fff', opacity: saving ? 0.6 : 1 }}
                onClick={confirmRescue} disabled={saving}
                onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#1a6b3a'; }}
                onMouseOut={e => { e.currentTarget.style.background = '#14562e'; }}
              >
                {saving ? <span className="spinner" style={{ width: '16px', height: '16px', borderTopColor: 'white' }} /> : 'Sim, resgatar histórico'}
              </button>
            </div>
          </div>
        ) : (
        <>
        {/* Type chips */}
        <div style={{ ...M.row, marginBottom: '16px' }}>
          <label style={M.label}>Tipo</label>
          <div style={M.chipRow}>
            {TYPES.map(t => (
              <Chip key={t.value} label={t.label} icon={t.icon}
                selected={form.type === t.value}
                onClick={() => set('type', t.value)}
              />
            ))}
          </div>
        </div>

        {/* Placa */}
        <div style={{ ...M.row, marginBottom: '16px' }}>
          <label style={M.label}>
            Placa
            {form.plate.length > 0 && form.plate.length < 7 && (
              <span style={{ marginLeft: '8px', color: '#e97117', fontWeight: 700, fontSize: '11px' }}>{form.plate.length}/7</span>
            )}
            {form.plate.length === 7 && (
              <span style={{ marginLeft: '8px', color: '#14562e', fontWeight: 700, fontSize: '11px' }}>✓</span>
            )}
          </label>
          <input
            style={{ ...M.input, textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600, fontFamily: "'DM Mono', monospace" }}
            placeholder="ABC-1234"
            value={form.plate}
            onChange={e => set('plate', formatPlate(e.target.value))}
            maxLength={7}
          />
        </div>

        {/* Marca + Modelo */}
        <div style={M.grid}>
          <div style={M.row}>
            <label style={M.label}>Marca {loadingBrands && <span style={{ fontSize: '10px', opacity: 0.6 }}>⏳</span>}</label>
            {form.manualEntry ? (
              <input
                style={M.input} placeholder="Ex: Chevrolet"
                value={form.brand} onChange={e => set('brand', e.target.value)}
              />
            ) : (
              <select style={M.select} value={form.brandCode} onChange={handleBrandSelect} disabled={loadingBrands}>
                <option value="">{loadingHint(loadingBrands, 'marca')}</option>
                {brands.map(b => <option key={b.codigo} value={b.codigo}>{b.nome}</option>)}
              </select>
            )}
          </div>
          <div style={M.row}>
            <label style={M.label}>Modelo {loadingModels && <span style={{ fontSize: '10px', opacity: 0.6 }}>⏳</span>}</label>
            {form.manualEntry ? (
              <input
                style={M.input} placeholder="Ex: Onix Plus 1.0 Turbo"
                value={form.model} onChange={e => set('model', e.target.value)}
              />
            ) : form.brandCode ? (
              <select style={M.select} value={form.modelCode} onChange={handleModelSelect} disabled={loadingModels}>
                <option value="">{loadingHint(loadingModels, 'modelo')}</option>
                {models.map(m => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
              </select>
            ) : (
              <input style={{ ...M.input, opacity: 0.5 }} placeholder="Selecione a marca primeiro" disabled />
            )}
          </div>
        </div>

        {/* Alerta contextual quando a marca não tem modelos na FIPE */}
        {showEmptyModelsAlert && (
          <div style={M.emptyAlert}>
            <span>🔍 Não encontramos modelos dessa marca na tabela FIPE.</span>
            <button type="button" style={M.manualLink} onClick={toggleManualEntry}>
              Cadastrar manualmente →
            </button>
          </div>
        )}

        {/* Link permanente de fallback (sempre visível, exceto quando já em modo manual) */}
        {!form.manualEntry && !showEmptyModelsAlert && (
          <div style={M.fallbackRow}>
            <button type="button" style={M.manualLink} onClick={toggleManualEntry}>
              Não encontrou seu veículo na lista? Cadastrar manualmente →
            </button>
          </div>
        )}
        {form.manualEntry && (
          <div style={M.fallbackRow}>
            <button type="button" style={M.manualLink} onClick={toggleManualEntry}>
              ← Voltar para busca automática na tabela FIPE
            </button>
          </div>
        )}

        {/* Ano + KM */}
        <div style={M.grid}>
          <div style={M.row}>
            <label style={M.label}>Ano {loadingYears && <span style={{ fontSize: '10px', opacity: 0.6 }}>⏳</span>}</label>
            {form.manualEntry ? (
              <input
                style={M.input} placeholder="2022" inputMode="numeric" maxLength={4}
                value={form.year || ''}
                onChange={e => set('year', e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            ) : form.modelCode ? (
              <select style={M.select} value={form.yearCode} onChange={handleYearSelect} disabled={loadingYears}>
                <option value="">{loadingHint(loadingYears, 'ano')}</option>
                {years.map(y => <option key={y.codigo} value={y.codigo}>{y.nome}</option>)}
              </select>
            ) : (
              <input style={{ ...M.input, opacity: 0.5 }} placeholder="Selecione o modelo" disabled />
            )}
          </div>
          <div style={M.row}>
            <label style={M.label}>KM atual</label>
            <input
              style={M.input} placeholder="60.000" inputMode="numeric"
              value={form.current_km ? form.current_km.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''}
              onChange={e => set('current_km', e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </div>

        {/* Cor */}
        <div style={{ ...M.row, marginTop: '16px' }}>
          <label style={M.label}>Cor</label>
          <input style={M.input} placeholder="Cinza pérola" value={form.color} onChange={e => set('color', e.target.value)} />
        </div>

        {/* Valor estimado (opcional) — só no cadastro manual, já que não há valor FIPE automático */}
        {form.manualEntry && (
          <div style={{ ...M.row, marginTop: '16px' }}>
            <label style={M.label}>Valor estimado (opcional)</label>
            <input
              style={M.input} placeholder="Ex: 45.000" inputMode="numeric"
              value={form.fipe_value ? String(form.fipe_value).replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''}
              onChange={e => set('fipe_value', parseInt(e.target.value.replace(/\D/g, '')) || null)}
            />
          </div>
        )}

        {/* FIPE badge */}
        {form.fipe_code && (
          <div style={M.fipeBadge}>
            <span>✓</span>
            <span>Identificado na tabela FIPE · Código {form.fipe_code}
              {form.fipe_value && ` · R$ ${form.fipe_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            </span>
          </div>
        )}

        {/* Dica do Nico */}
        {form.manualEntry ? (
          <p style={M.tip}>
            <b style={{ color: '#14562e' }}>🐕 Dica do Nico:</b> cadastro manual — sem valor de mercado automático da FIPE. Você pode informar um valor estimado acima, se quiser.
          </p>
        ) : !form.fipe_code && (
          <p style={M.tip}>
            <b style={{ color: '#14562e' }}>🐕 Dica do Nico:</b> selecione marca, modelo e ano para buscar automaticamente o valor na tabela FIPE.
          </p>
        )}

        {/* Actions */}
        <div style={M.actions}>
          <button style={{ ...M.btn, background: 'transparent', color: '#183d24' }} onClick={() => { setRescuePrompt(null); onClose(); }}
            onMouseOver={e => { e.currentTarget.style.background = '#ede4d1'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
          >Cancelar</button>
          <button
            style={{ ...M.btn, background: '#14562e', color: '#fff', opacity: saving ? 0.6 : 1 }}
            onClick={handleSubmit} disabled={saving}
            onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#1a6b3a'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#14562e'; }}
          >
            {saving ? <span className="spinner" style={{ width: '16px', height: '16px', borderTopColor: 'white' }} /> : 'Adicionar à garagem'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
